import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, Shield, HardDrive, Users, Cpu } from 'lucide-react';
import api from '../utils/api';

const PLAN_STYLES = {
  dirt:      { emoji: '🌱', color: 'border-gray-700' },
  stone:     { emoji: '⛰️',  color: 'border-gray-600' },
  iron:      { emoji: '⚙️',  color: 'border-green-500', popular: true },
  diamond:   { emoji: '💎', color: 'border-blue-500' },
  netherite: { emoji: '🔥', color: 'border-orange-500' },
};

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/plans').then(({ data }) => setPlans(data.plans)).finally(() => setLoading(false));
  }, []);

  const getPrice = (plan) => {
    if (billing === 'annually' && plan.price_annually) return (parseFloat(plan.price_annually) / 12).toFixed(2);
    if (billing === 'quarterly' && plan.price_quarterly) return (parseFloat(plan.price_quarterly) / 3).toFixed(2);
    return parseFloat(plan.price_monthly).toFixed(2);
  };

  const getSavings = (plan) => {
    if (billing === 'annually' && plan.price_annually) {
      const saved = ((parseFloat(plan.price_monthly) * 12) - parseFloat(plan.price_annually)).toFixed(2);
      return `Save €${saved}/yr`;
    }
    if (billing === 'quarterly' && plan.price_quarterly) {
      const saved = ((parseFloat(plan.price_monthly) * 3) - parseFloat(plan.price_quarterly)).toFixed(2);
      return `Save €${saved}/qtr`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black text-white mb-4">Simple, honest pricing</h1>
          <p className="text-gray-400 text-lg mb-8">No setup fees. No hidden costs. Cancel anytime.</p>

          {/* Billing toggle */}
          <div className="inline-flex bg-gray-900 border border-gray-800 rounded-xl p-1 gap-1">
            {['monthly', 'quarterly', 'annually'].map((b) => (
              <button
                key={b}
                onClick={() => setBilling(b)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors relative ${
                  billing === b ? 'bg-green-500 text-gray-950' : 'text-gray-400 hover:text-white'
                }`}
              >
                {b}
                {b === 'annually' && (
                  <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                    -20%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plans */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 animate-pulse h-96" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {plans.map((plan) => {
              const style = PLAN_STYLES[plan.slug] || { emoji: '🎮', color: 'border-gray-700' };
              const savings = getSavings(plan);
              return (
                <div
                  key={plan.id}
                  className={`relative bg-gray-900 border-2 rounded-2xl p-6 flex flex-col transition-all hover:scale-105 ${style.color}`}
                >
                  {style.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-green-500 text-gray-950 text-xs font-black px-4 py-1 rounded-full whitespace-nowrap">
                      MOST POPULAR
                    </div>
                  )}

                  <div className="text-3xl mb-3">{style.emoji}</div>
                  <h3 className="text-white font-black text-xl mb-1">{plan.name}</h3>
                  <p className="text-gray-500 text-xs mb-4">{plan.description}</p>

                  <div className="mb-1">
                    <span className="text-4xl font-black text-white">€{getPrice(plan)}</span>
                    <span className="text-gray-500 text-sm">/mo</span>
                  </div>
                  {savings && (
                    <div className="text-green-400 text-xs font-semibold mb-4">{savings}</div>
                  )}
                  {!savings && <div className="mb-4" />}

                  <ul className="space-y-2.5 text-sm text-gray-400 mb-6 flex-1">
                    <li className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {plan.ram_mb / 1024}GB RAM
                    </li>
                    <li className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {plan.disk_mb / 1024}GB Storage
                    </li>
                    <li className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {plan.player_slots} player slots
                    </li>
                    <li className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-green-400 flex-shrink-0" />
                      Instant setup
                    </li>
                    <li className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-400 flex-shrink-0" />
                      DDoS protection
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      Full panel access
                    </li>
                  </ul>

                  <Link
                    to={`/order/${plan.slug}?billing=${billing}`}
                    className={`text-center py-3 rounded-xl text-sm font-bold transition-colors ${
                      style.popular
                        ? 'bg-green-500 hover:bg-green-400 text-gray-950'
                        : 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-700'
                    }`}
                  >
                    Get {plan.name}
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ */}
        <div className="mt-20 max-w-2xl mx-auto">
          <h2 className="text-2xl font-black text-white text-center mb-8">Common questions</h2>
          <div className="space-y-4">
            {[
              { q: 'How fast is server setup?', a: 'Your Minecraft server is live within 30 seconds of payment. No waiting, no manual steps.' },
              { q: 'Can I upgrade my plan later?', a: 'Yes — upgrade anytime from your dashboard. The price difference is prorated automatically.' },
              { q: 'What Minecraft versions are supported?', a: 'All versions from 1.8 to the latest release. Paper, Vanilla, Forge, and Fabric supported.' },
              { q: 'Do you offer refunds?', a: 'Yes, we offer a 24-hour money-back guarantee on all plans if you\'re not satisfied.' },
              { q: 'Can I cancel anytime?', a: 'Absolutely. Cancel from your dashboard at any time with no cancellation fees.' },
            ].map(({ q, a }) => (
              <div key={q} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-2">{q}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
